import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import _ from 'lodash';
import { PRODUCT_CODE } from '../../../../enums/productCode.enum';
import { createPaymentRedirect } from '../../../../helper/createPaymentRedirect';
import { handlePaymentRedirect } from '../../../../helper/handlePaymentRedirect';
import { setPendingPayment } from '../../../../helper/paymentPendingStorage';
import { getIsUsingReservations } from '../../../../helper/UtilityFunctions';
import UserService from '../../../../services/User.service';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { selectPrescriptionInfo, selectUserInformation, setPrescriptionInfo } from '../../../../store/reducers';
import { initialState } from '../../../../store/reducers/followUpPrescriptionSlice';
import type { ReducerAction } from '../../../../types/navigationIndex.types';
import { Checkout } from '../../../organisms/Checkout/Checkout';
import { ProgressStep, ProgressSteps } from '../../../organisms/ProgressSteps/ProgressSteps';
import classes from './SendFollowUpPrescription.module.scss';

const cx = classNames.bind(classes);

type SendFollowUpPrescriptionProps = {
  setCurrentPage: React.Dispatch<ReducerAction>;
};

export function SendFollowUpPrescription({ setCurrentPage }: SendFollowUpPrescriptionProps) {
  const userInformation = useAppSelector(selectUserInformation);
  const prescriptionInfo = useAppSelector(selectPrescriptionInfo);
  const [lastMedicationID, setLastMedicationID] = useState<string>();
  const dispatch = useAppDispatch();
  const isUsingReservations = getIsUsingReservations();

  const productCode = isUsingReservations
    ? PRODUCT_CODE.RESERVATION_PRESCRIPTION_ONLINE
    : PRODUCT_CODE.PRESCRIPTION_ONLINE;

  useEffect(() => {
    if (userInformation && userInformation.medication && userInformation.medication.length > 0) {
      setLastMedicationID(userInformation.medication[0].id);
    }
  }, [userInformation]);

  const handleBackButton = () => {
    const reducerType = {
      type: '[UPDATE]',
      payload: {
        followUpPrescriptionsIndex: prescriptionInfo?.medicationAvailable === 'changeMedication' ? 8 : 7,
      },
    };
    setCurrentPage(reducerType);
  };

  const preparaDataForSending = () => {
    const finalData = _.cloneDeep(prescriptionInfo);
    finalData.generalSideEffects = prescriptionInfo.generalSideEffects.toString();
    finalData.psychologicalSideEffects = prescriptionInfo.psychologicalSideEffects.toString();
    finalData.medicationId = lastMedicationID;

    if (prescriptionInfo.preparationQuestion && Array.isArray(prescriptionInfo.preparationQuestion)) {
      for (let i = 0; i < prescriptionInfo.preparationQuestion.length; i++) {
        const currentPreparation = prescriptionInfo.preparationQuestion[i];
        finalData.preparationQuestion[i] = { preparationListItemId: currentPreparation.id };

        if (
          currentPreparation.changeThisPreparation !== undefined ||
          currentPreparation.exchangeWithThisPreparation !== undefined
        ) {
          finalData.preparationQuestion[i].changeThisPreparation = currentPreparation.changeThisPreparation;
          finalData.preparationQuestion[i].exchangeWithThisPreparation = currentPreparation.exchangeWithThisPreparation;
        }
      }
    }

    return finalData;
  };

  const save = async (couponCode?: string): Promise<any> => {
    const redirectUrl = createPaymentRedirect();
    const preparedDataForSending = preparaDataForSending();

    const response = await UserService.followPrescription({
      ...preparedDataForSending,
      redirectUrl,
      couponCode,
    });

    if (response.data?.redirectUrl) {
      setPendingPayment({
        startedAt: Date.now(),
        sourcePath: `${window.location.pathname}${window.location.search}`,
        novalnetUrl: response.data.redirectUrl,
        contextType: 'prescription',
      });
      handlePaymentRedirect(response.data.redirectUrl);

      return {
        success: true,
        redirectUrl: response.data.redirectUrl,
        navigateTo: !response.data.redirectUrl ? redirectUrl : undefined,
      };
    }

    if (response.error) {
      console.error(response.message);

      return { success: false, error: response.message };
    }
    if (!response.error) {
      const reducerType = {
        type: '[UPDATE]',
        payload: {
          followUpPrescriptionsIndex: 10,
        },
      };
      dispatch(setPrescriptionInfo(initialState));
      setCurrentPage(reducerType);
    }
  };

  const details: Array<{ label: string; value: string | React.ReactNode }> = [];

  if (prescriptionInfo && prescriptionInfo.preparationQuestion && Array.isArray(prescriptionInfo.preparationQuestion)) {
    prescriptionInfo.preparationQuestion.forEach((medication: any) => {
      if (medication.changeThisPreparation) {
        details.push({
          label: 'Gewünschte Medikamentenalternative',
          value: medication.exchangeWithThisPreparation || 'Nicht angegeben',
        });
      } else {
        const dosageString = `${medication.dosageSingle || ''}${medication.dosageUnit || ''}`;
        const quantityString = `${medication.quantity || ''}${medication.dosageUnit || ''}`;
        details.push({
          label: 'Medikament',
          value: (
            <>
              <div key={`name-${medication.id || medication.name}`}>{medication.name || 'Unbekannt'}</div>
              <div key={`dosage-${medication.id || medication.name}`}>Dosierung: {dosageString}</div>
              <div key={`quantity-${medication.id || medication.name}`}>Menge: {quantityString}</div>
            </>
          ),
        });
      }
    });
  }

  if (details.length === 0) {
    details.push({ label: 'Rezeptdetails', value: 'Keine Medikamente in dieser Rezeptanfrage aufgeführt.' });
  }

  return (
    <>
      <div className={cx('SendFollowUpPrescription')}>
        <ProgressSteps>
          <ProgressStep step={1} title='Fragebogen' active />
          <ProgressStep step={2} title='Apotheke' active />
          <ProgressStep step={3} title='Lieferbarkeit' active />
          <ProgressStep step={4} title='Bestätigung' active />
          <ProgressStep step={5} title='Zahlung' />
        </ProgressSteps>
      </div>
      <Checkout
        productCode={productCode}
        itemDetailsTitle='Ihre Rezeptdetails'
        itemDetails={details}
        handleSubmit={save}
        handleBack={handleBackButton}
      />
    </>
  );
}
